import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { zahlAus, mitEinheit } from "../layoutEntwurf.js";

/** Die DOM-Kennung zu einem Entwurfspfad — `seitenraender.oben` → `lay-seitenraender-oben`. */
export function feldId(pfad: string): string {
  return `lay-${pfad.replace(/[^A-Za-z0-9]+/g, "-")}`;
}

/**
 * Ein Zahlenfeld mit Einheit (mm, pt, %). Der SYSTEMWERT steht als Platzhalter; ein
 * eigener Wert ist hervorgehoben und lässt sich mit „Systemwert" zurücknehmen. Die
 * Eingabe bleibt Text, solange getippt wird (auch „18," ist ein Zwischenstand), und geht
 * bei jeder Änderung als „18.5mm" in den Entwurf.
 */
export function EinheitFeld(p: {
  id: string; label: string; einheit: "mm" | "pt" | "%"; wert: unknown; systemwert: string;
  fehler: string | undefined; onWert: (neu: string | undefined) => void;
  onVerlassen: () => void;
}): ReactElement {
  const eigen = p.wert !== undefined;
  const [text, setText] = useState(zahlAus(p.wert, p.einheit));
  // Kommt der Wert von aussen (Verwerfen, Übernehmen, Systemwert), folgt das Feld.
  useEffect(() => {
    setText((alt) => (mitEinheit(alt, p.einheit) === p.wert ? alt : zahlAus(p.wert, p.einheit)));
  }, [p.wert, p.einheit]);
  const fehlerId = `${p.id}-fehler`;
  return (
    <div className="lay__feld">
      <label className="lay__label" htmlFor={p.id}>{p.label}</label>
      <div className="lay__eingabezeile">
        <input id={p.id} type="text" inputMode="decimal"
          className={`lay__eingabe${eigen ? " lay__eingabe--eigen" : ""}`}
          value={text} placeholder={zahlAus(p.systemwert, p.einheit)}
          aria-invalid={p.fehler !== undefined}
          {...(p.fehler === undefined ? {} : { "aria-describedby": fehlerId })}
          onChange={(e) => { setText(e.target.value); p.onWert(mitEinheit(e.target.value, p.einheit)); }}
          onBlur={p.onVerlassen} />
        <span className="lay__einheit">{p.einheit}</span>
        {eigen && (
          <button type="button" className="lay__systemwert"
            aria-label={`${p.label}: Systemwert`}
            onClick={() => { setText(""); p.onWert(undefined); p.onVerlassen(); }}>
            Systemwert
          </button>
        )}
      </div>
      {p.fehler !== undefined && (
        <span id={fehlerId} className="lay__feldfehler" role="alert">{p.fehler}</span>
      )}
    </div>
  );
}

/** Eine Auswahl, deren leerer Wert „Systemwert" heisst. Eine Wahl ist eine fertige
 *  Eingabe — der Aufrufer fordert die Vorschau gleich an. */
export function WahlFeld(p: {
  id: string; label: string; wert: string; optionen: readonly { wert: string; text: string }[];
  fehler: string | undefined; onWahl: (wert: string) => void;
}): ReactElement {
  const fehlerId = `${p.id}-fehler`;
  return (
    <div className="lay__feld">
      <label className="lay__label" htmlFor={p.id}>{p.label}</label>
      <select id={p.id} className={`lay__wahl${p.wert !== "" ? " lay__eingabe--eigen" : ""}`}
        value={p.wert} aria-invalid={p.fehler !== undefined}
        {...(p.fehler === undefined ? {} : { "aria-describedby": fehlerId })}
        onChange={(e) => p.onWahl(e.target.value)}>
        {p.optionen.map((o) => <option key={o.wert} value={o.wert}>{o.text}</option>)}
      </select>
      {p.fehler !== undefined && (
        <span id={fehlerId} className="lay__feldfehler" role="alert">{p.fehler}</span>
      )}
    </div>
  );
}

/** Ein Schalter: zeigt den WIRKSAMEN Wert (eigen, sonst System); ein eigener Wert ist
 *  markiert und lässt sich zurücknehmen. */
export function SchalterFeld(p: {
  id: string; label: string; wert: unknown; systemwert: boolean; fehler: string | undefined;
  onWert: (neu: boolean | undefined) => void;
}): ReactElement {
  const eigen = typeof p.wert === "boolean";
  const an = eigen ? p.wert as boolean : p.systemwert;
  return (
    <div className="lay__feld">
      <div className="lay__eingabezeile">
        <input id={p.id} type="checkbox" checked={an} aria-invalid={p.fehler !== undefined}
          {...(p.fehler === undefined ? {} : { "aria-describedby": `${p.id}-fehler` })}
          onChange={(e) => p.onWert(e.target.checked)} />
        <label htmlFor={p.id} className={eigen ? "lay__label lay__label--eigen" : "lay__label"}>
          {p.label}
        </label>
        {eigen && (
          <button type="button" className="lay__systemwert"
            aria-label={`${p.label}: Systemwert`} onClick={() => p.onWert(undefined)}>
            Systemwert ({p.systemwert ? "an" : "aus"})
          </button>
        )}
      </div>
      {p.fehler !== undefined && (
        <span id={`${p.id}-fehler`} className="lay__feldfehler" role="alert">{p.fehler}</span>
      )}
    </div>
  );
}

/** Der Wortlaut der Markdown-Regel und des Sofort-Hinweises (Fixrunde 1, W1). */
const MARKDOWN_HILFE = "Markdown im Beleg-Tagsatz; kein HTML — die Zeichen < und > sind nicht erlaubt";
const SPITZE_KLAMMERN = "Die Zeichen < und > sind nicht erlaubt.";

/** Ein Text (einzeilig oder mehrzeilig). Leer heisst „Systemwert"; der steht als
 *  Platzhalter. `hinweis` sagt, woher ein Platzhalter stammt (Erbe der Korrektur).
 *
 *  Fixrunde 1 (W1), beides optional: `max` zeigt einen Zähler „n / max" (Warnfarbe ab
 *  max − 100); `markdown` nennt die Regel des Beleg-Tagsatzes unter dem Feld und weist
 *  `<`/`>` sofort am Feld ab, noch vor dem Rundlauf — maßgeblich bleibt der Server. */
export function TextFeld(p: {
  id: string; label: string; wert: unknown; systemwert: string; mehrzeilig: boolean;
  hinweis?: string | undefined; fehler: string | undefined;
  max?: number | undefined; markdown?: boolean | undefined;
  onWert: (neu: string | undefined) => void; onVerlassen: () => void;
}): ReactElement {
  const eigen = typeof p.wert === "string";
  const text = eigen ? p.wert as string : "";
  const fehlerId = `${p.id}-fehler`;
  const hilfeId = `${p.id}-hilfe`;
  const zaehlerId = `${p.id}-zaehler`;
  const klammerId = `${p.id}-klammern`;
  const klammern = p.markdown === true && /[<>]/.test(text);
  const beschrieben = [
    p.markdown === true ? hilfeId : null,
    p.max !== undefined ? zaehlerId : null,
    klammern ? klammerId : null,
    p.fehler !== undefined ? fehlerId : null,
  ].filter((x): x is string => x !== null).join(" ");
  const gemeinsam = {
    id: p.id,
    className: `lay__text${eigen ? " lay__eingabe--eigen" : ""}`,
    value: text,
    placeholder: p.systemwert,
    "aria-invalid": p.fehler !== undefined || klammern,
    ...(beschrieben === "" ? {} : { "aria-describedby": beschrieben }),
    onBlur: p.onVerlassen,
  };
  const zaehlerArt = p.max === undefined ? ""
    : text.length > p.max ? " lay__textzaehler--fehler"
    : text.length >= p.max - 100 ? " lay__textzaehler--warnung" : "";
  return (
    <div className="lay__feld">
      <label className="lay__label" htmlFor={p.id}>{p.label}</label>
      {p.mehrzeilig
        ? <textarea {...gemeinsam} rows={3}
            onChange={(e) => p.onWert(e.target.value === "" ? undefined : e.target.value)} />
        : <input {...gemeinsam} type="text"
            onChange={(e) => p.onWert(e.target.value === "" ? undefined : e.target.value)} />}
      {p.hinweis !== undefined && !eigen && <span className="lay__erbe">{p.hinweis}</span>}
      {(p.markdown === true || p.max !== undefined) && (
        <div className="lay__textfuss">
          {p.markdown === true && <span id={hilfeId} className="lay__hilfe">{MARKDOWN_HILFE}</span>}
          {p.max !== undefined && (
            <span id={zaehlerId} className={`lay__textzaehler${zaehlerArt}`}>
              {`${text.length} / ${p.max}`}
            </span>
          )}
        </div>
      )}
      {eigen && (
        <button type="button" className="lay__systemwert" aria-label={`${p.label}: Systemwert`}
          onClick={() => { p.onWert(undefined); p.onVerlassen(); }}>Systemwert</button>
      )}
      {klammern && (
        <span id={klammerId} className="lay__feldfehler" role="alert">{SPITZE_KLAMMERN}</span>
      )}
      {p.fehler !== undefined && (
        <span id={fehlerId} className="lay__feldfehler" role="alert">{p.fehler}</span>
      )}
    </div>
  );
}
